import * as React from "react";
import { Fragment } from "react";
import {
  Col,
  Form,
  Input,
  Row,
  Select,
  InputNumber,
  Switch,
  Divider
} from "antd";
import {
  createRequest,
  fetchItemRequest,
  updateRequest
} from "@actions/hyperLinkMenu";
import { HyperLinkMenu } from "@models/hyperLinkMenu";
import { FormattedMessage } from "react-intl";
import { HyperLinkMenuLocationOptions } from "@models/hyperLinkMenu";
import { PlatformOptions, SelectionMode } from "@models";
import { NavigationConfiguration } from "@models/navigation";
import Navigation from "@components/custom/Navigation";
import withFormWrapper from "../../hocs/withFormWrapper";

const { Option } = Select;

interface PropsFromState {
  loading: boolean;
  errors?: string;
  form: any;
  hyperLinkMenu?: HyperLinkMenu;
}

interface PropsFromDispatch {
  createRequest: typeof createRequest;
  updateRequest: typeof updateRequest;
  fetchItemRequest: typeof fetchItemRequest;
}

type HyperLinkMenuFormAllProps = PropsFromState & PropsFromDispatch;

interface HyperLinkMenuFormStateShape extends HyperLinkMenu {
  visible: boolean;
  id: string;
  display_title: string;
  platforms: string;
  location: string;
  order: number;
  is_active: boolean;
  configuration: NavigationConfiguration;
}

class HyperLinkMenuForm extends React.Component<
  HyperLinkMenuFormAllProps,
  HyperLinkMenuFormStateShape
> {
  navigationRef = null;

  constructor(props) {
    super(props);
    const { hyperLinkMenu } = props;
    this.state = {
      ...this.getStateFromProps(hyperLinkMenu)
    };
  }

  getStateFromProps = (hyperLinkMenu: HyperLinkMenu | null): object => {
    //TODO check just once and set default values instead of inline if for each property!
    const obj = hyperLinkMenu || {};
    return {
      visible: false,
      display_title: obj.display_title || "",
      configuration: obj.configuration || null,
      platforms: obj.platforms || "",
      location: obj.location || "",
      order: obj.order || "",
      is_active: obj.is_active || true,
      id: obj.id
    };
  };

  /**
   * This function gets navigation configuration values
   * return an object or null
   * Null means that there is a validation issue in the Navigation Component
   */
  getNavigationConfig = () => {
    if (this.navigationRef) {
      const navigationConfig = this.navigationRef.getConfig();
      if (navigationConfig) {
        return navigationConfig;
      } else {
        return null;
      }
    } else {
      return {};
    }
  };

  getValues = () => {
    const navigationConfig = this.getNavigationConfig();
    let returnValues = null;
    this.props.form.validateFieldsAndScroll((err, values) => {
      if (!err && !!navigationConfig) {
        returnValues = {
          ...values,
          is_active: this.state.is_active,
          platforms: values.platforms.join(","),
          configuration: navigationConfig.navigation_configuration
        };
      }
    });

    return {
      payload: returnValues,
      id: this.state.id
    };
  };

  onStatusChange(is_active: boolean) {
    this.setState({
      is_active
    });
  }

  render() {
    const { getFieldDecorator } = this.props.form;
    return (
      <Fragment>
        <Form>
          <Row>
            <Col span={12}>
              <Form.Item
                label={
                  <FormattedMessage
                    id="app.display_title"
                    defaultMessage={`Display Title`}
                  />
                }
              >
                {getFieldDecorator("display_title", {
                  initialValue: this.state.display_title,
                  rules: [
                    {
                      whitespace: true,
                      required: true,
                      message: (
                        <FormattedMessage
                          id="app.display_title_is_required"
                          defaultMessage={`Display title is required`}
                        />
                      )
                    }
                  ]
                })(<Input />)}
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={
                  <FormattedMessage
                    id="app.select_platform"
                    defaultMessage={`Select Platform`}
                  />
                }
              >
                {getFieldDecorator("platforms", {
                  initialValue: this.state.platforms
                    ? this.state.platforms.replace(/\s/g, "").split(",")
                    : [],
                  rules: [
                    {
                      // whitespace: true,
                      required: true,
                      message: (
                        <FormattedMessage
                          id="app.platform_is_required"
                          defaultMessage={`Platform is required`}
                        />
                      )
                    }
                  ]
                })(
                  <Select mode={SelectionMode.MULTIPLE} showArrow={true}>
                    {PlatformOptions.map((opt, index) => (
                      <Option key={opt.value}>{opt.label}</Option>
                    ))}
                  </Select>
                )}
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={
                  <FormattedMessage
                    id="app.display_location"
                    defaultMessage={`Display Location`}
                  />
                }
              >
                {getFieldDecorator("location", {
                  initialValue: this.state.location,
                  rules: [
                    {
                      // whitespace: true,
                      required: true,
                      message: (
                        <FormattedMessage
                          id="app.display_location_is_required"
                          defaultMessage={`Display location is required`}
                        />
                      )
                    }
                  ]
                })(
                  <Select showArrow={true}>
                    {HyperLinkMenuLocationOptions.map(opt => (
                      <Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Option>
                    ))}
                  </Select>
                )}
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                label={
                  <FormattedMessage
                    id="app.display_order"
                    defaultMessage={`Display Order`}
                  />
                }
              >
                {getFieldDecorator("order", {
                  initialValue: this.state.order,
                  rules: [
                    {
                      required: true,
                      message: (
                        <FormattedMessage
                          id="app.display_order_is_required"
                          defaultMessage={`Display order is required`}
                        />
                      )
                    }
                  ]
                })(<InputNumber />)}
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                label={
                  <FormattedMessage id="app.active" defaultMessage={`Active`} />
                }
              >
                <Switch
                  defaultChecked={this.state.is_active}
                  onChange={this.onStatusChange.bind(this)}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
        <div>
          <Divider dashed orientation="left">
            <FormattedMessage
              id="app.navigation_settings"
              defaultMessage={`Navigation Settings`}
            />
          </Divider>
          <Navigation
            wrappedComponentRef={ref => (this.navigationRef = ref)}
            widgetType={this.props.type}
            navigation_configuration={this.state.configuration}
            hasButtonConfig={false}
            required={true}
          />
        </div>
      </Fragment>
    );
  }
}

export default withFormWrapper(
  Form.create({ name: "hyperLinkMenu-form" })(HyperLinkMenuForm)
);
